import { useState, useEffect, useCallback } from "react";
import { useUsersApi } from "@/hooks/useUsersApi";
import { useBrandsApi } from "@/hooks/useBrandsApi";
import { useSettingsFilters } from "@/hooks/useSettingsFilters";
import { useSettingsModals } from "@/hooks/useSettingsModals";
import LoadingSpinner from "@/components/orders/LoadingSpinner";
import ErrorMessage from "@/components/orders/ErrorMessage";
import UserInvitationModal from "@/components/modals/UserInvitationModal";
import UserEditModal from "@/components/modals/UserEditModal";
import SettingsContent from "./partials/SettingsContent";
import { PageHeader } from "@/components";

export default function Settings() {
  const {
    users,
    loading: usersLoading,
    error: usersError,
    fetchUsers,
  } = useUsersApi();
  const {
    brands,
    loading: brandsLoading,
    error: brandsError,
    fetchAllBrands,
  } = useBrandsApi();
  const [localUsers, setLocalUsers] = useState(users);
  const [tab, setTab] = useState("usuarios");
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Custom hooks
  const filters = useSettingsFilters();
  const modals = useSettingsModals();
  useEffect(() => {
    if (brands.length === 0) {
      fetchAllBrands();
    }
  }, [brands]);
  // Función para cargar datos iniciales
  const loadInitialData = useCallback(async () => {
    if (!isInitialLoad) return;

    try {
      await Promise.all([fetchUsers(), fetchAllBrands()]);
    } finally {
      setIsInitialLoad(false);
    }
  }, [isInitialLoad, fetchUsers, fetchAllBrands]);

  // Cargar datos iniciales
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Sincronizar usuarios locales con los usuarios del hook
  useEffect(() => {
    setLocalUsers(users);
  }, [users]);

  // Función para agregar un nuevo usuario a la lista local
  const handleUserCreated = (newUser: any) => {
    setLocalUsers((prevUsers) =>
      [...prevUsers, newUser].sort((a, b) =>
        `${a.first_name} ${a.last_name}`.localeCompare(
          `${b.first_name} ${b.last_name}`,
        ),
      ),
    );
  };

  // Función para actualizar un usuario en la lista local (desactivar/reactivar)
  const handleUserUpdated = (userId: number, isReactivating: boolean) => {
    setLocalUsers((prevUsers) =>
      prevUsers.map((user) =>
        user.id === userId ? { ...user, active: isReactivating } : user,
      ),
    );
  };

  // Función para actualizar un usuario editado en la lista local
  const handleUserEdited = (updatedUser: any) => {
    setLocalUsers((prevUsers) => {
      return prevUsers.map((user) =>
        user.id === updatedUser.id ? { ...user, ...updatedUser } : user,
      );
    });
  };

  if (isInitialLoad || usersLoading || brandsLoading) {
    return <LoadingSpinner />;
  }

  if (usersError || brandsError) {
    return (
      <ErrorMessage
        message={usersError || brandsError || "Error desconocido"}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <PageHeader
        title="Ajustes"
        description="Invita y ajusta los detalles de los usuarios"
      />

      <SettingsContent
        tab={tab}
        setTab={setTab}
        filters={filters}
        brands={brands}
        localUsers={localUsers}
        onInviteClick={() => modals.setIsInviteModalOpen(true)}
        onUserUpdated={handleUserUpdated}
        onEditUser={modals.handleEditUser}
      />

      {/* User Invitation Modal */}
      <UserInvitationModal
        isOpen={modals.isInviteModalOpen}
        onClose={() => modals.setIsInviteModalOpen(false)}
        onUserCreated={handleUserCreated}
        brands={brands}
      />

      {/* User Edit Modal */}
      <UserEditModal
        isOpen={modals.isEditModalOpen}
        onClose={modals.handleCloseEditModal}
        user={modals.selectedUserForEdit}
        onUserUpdated={handleUserEdited}
        brands={brands}
      />
    </div>
  );
}
